import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Building2,
  User,
  Calendar,
  FileText,
  Home,
  Plus,
  Trash2,
  Pencil,
} from "lucide-react";
import { Card, CardContent } from "@/frontend/components/ui/card";
import { Badge } from "@/frontend/components/ui/badge";
import { Button } from "@/frontend/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/frontend/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/frontend/components/ui/dialog";
import { ScrollArea, ScrollBar } from "@/frontend/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/frontend/components/ui/tooltip";
import { cn } from "@/frontend/lib/utils";
import { toast as sonnerToast } from "sonner";
import { useAdminResidences } from "@/backend/hooks/admin/useAdminResidences";
import {
  useAdminBookings,
  useDeleteAdminBooking,
  useApproveBookingWithStudent,
  useUpdateBookingStatus,
  useMoveBookingUnit,
  type StudentCredential,
} from "@/backend/hooks/admin/useAdminBookings";
import { useAdminRoomTypes } from "@/backend/hooks/admin/useAdminRoomTypes";
import { sendBookingApprovedEmail, sendBookingRejectionEmail } from "@/backend/services/email/emailClient";
import { StudentCredentialsModal } from "@/frontend/components/admin/StudentCredentialsModal";
import { GuestDetailsBlock } from "@/frontend/components/admin/GuestDetailsBlock";
import { getRoomLabel } from "@/backend/lib/roomUtils";
import { Textarea } from "@/frontend/components/ui/textarea";
import type { Booking } from "@/shared/types";
import {
  CreateAdminBookingDialog,
  type AdminBookingPrefill,
} from "@/frontend/components/admin/CreateAdminBookingDialog";
import {
  type OccupancyBar,
  type StudentInfo,
  type RoomRow,
  type AvailabilityFilters,
} from "@/backend/hooks/admin/useAdminAvailabilityCalendar";
import {
  useYearCalendarData,
  MONTH_WIDTH_PX,
} from "@/backend/hooks/admin/useYearCalendarData";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// ─── Typology grouping ─────────────────────────────────────────────────────────

type CalendarGroup =
  | { kind: "single"; row: RoomRow }
  | { kind: "typology"; gkey: string; typologyKey: string; units: RoomRow[] };

/** A flattened calendar row — both the label column and the timeline iterate this. */
type DisplayRow =
  | { kind: "single"; row: RoomRow }
  | { kind: "group"; gkey: string; typologyKey: string; rep: RoomRow; unitCount: number; expanded: boolean }
  | { kind: "unit"; row: RoomRow; number: number };

/** Group calendar rows: typology units collapse together, others stay standalone. */
function groupRoomRows(rows: RoomRow[]): CalendarGroup[] {
  const groups: CalendarGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const row of rows) {
    const typologyKey = row.typologyKey?.trim();
    if (typologyKey) {
      const gkey = `${row.residenceId}|${typologyKey}`;
      const at = indexByKey.get(gkey);
      if (at !== undefined) {
        (groups[at] as Extract<CalendarGroup, { kind: "typology" }>).units.push(row);
      } else {
        indexByKey.set(gkey, groups.length);
        groups.push({ kind: "typology", gkey, typologyKey, units: [row] });
      }
    } else {
      groups.push({ kind: "single", row });
    }
  }
  // Stable per-unit ordering (1, 2, …) by creation time.
  for (const group of groups) {
    if (group.kind === "typology") {
      group.units.sort((a, b) => a.createdAtMillis - b.createdAtMillis);
    }
  }
  return groups;
}

/** Flatten groups into renderable rows, expanding the typologies the user opened. */
function flattenGroups(groups: CalendarGroup[], expanded: Set<string>): DisplayRow[] {
  const out: DisplayRow[] = [];
  for (const group of groups) {
    if (group.kind === "single") {
      out.push({ kind: "single", row: group.row });
      continue;
    }
    const isExpanded = expanded.has(group.gkey);
    out.push({
      kind: "group",
      gkey: group.gkey,
      typologyKey: group.typologyKey,
      rep: group.units[0],
      unitCount: group.units.length,
      expanded: isExpanded,
    });
    if (isExpanded) {
      group.units.forEach((row, i) => out.push({ kind: "unit", row, number: i + 1 }));
    }
  }
  return out;
}

// ─── Month Header ──────────────────────────────────────────────────────────────

interface MonthHeaderProps {
  /** Zero-based month of "today", or null when the displayed year isn't the current one. */
  currentMonthIndex: number | null;
}

function MonthHeader({ currentMonthIndex }: MonthHeaderProps) {
  return (
    <div className="flex border-b bg-muted/20 select-none sticky top-0 z-10">
      {MONTH_LABELS.map((label, i) => {
        const isCurrent = currentMonthIndex === i;
        return (
          <div
            key={label}
            className={cn(
              "flex-shrink-0 h-9 flex items-center justify-center border-r last:border-r-0",
              "text-xs font-medium",
              isCurrent
                ? "bg-secondary/15 text-secondary font-bold"
                : "text-muted-foreground"
            )}
            style={{ width: MONTH_WIDTH_PX }}
          >
            {label}
          </div>
        );
      })}
    </div>
  );
}

// ─── Contract Bar ──────────────────────────────────────────────────────────────

interface ContractBarProps {
  bar: OccupancyBar;
  onBarClick: (bar: OccupancyBar) => void;
}

// Row height: base 56px for one lane; each extra lane of stacked overlapping
// bars adds 48px (40px bar + 8px gap) so simultaneous bookings stay visible.
const BAR_LANE_PX = 48;
const ROW_PAD_PX = 8;
const rowHeightPx = (row: RoomRow): number =>
  ROW_PAD_PX + Math.max(1, row.laneCount ?? 1) * BAR_LANE_PX;

function barStyle(bar: OccupancyBar): string {
  switch (bar.source) {
    case "admin-block":
      // Hatched slate so a block reads unmistakably as "taken offline".
      return "bg-slate-300 text-slate-800 border-2 border-dashed border-slate-500 [background-image:repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(100,116,139,0.18)_5px,rgba(100,116,139,0.18)_10px)]";
    case "admin-booking":
      return "bg-amber-300 text-amber-900 shadow-sm";
    case "guest-booking":
      // Approved guest booking that doesn't have a contract yet.
      return "bg-emerald-300 text-emerald-900 shadow-sm";
    case "pending-booking":
      // Demand, not occupancy: pending bookings don't block availability.
      return "bg-orange-100 text-orange-800 border-2 border-dashed border-orange-400";
    case "contract":
    default:
      return bar.contractStatus === "active"
        ? "bg-secondary text-secondary-foreground shadow-sm"
        : "bg-muted text-muted-foreground border-2 border-muted-foreground/40";
  }
}

function ContractBar({ bar, onBarClick }: ContractBarProps) {
  const kind =
    bar.source === "admin-block"
      ? "Admin block"
      : bar.source === "admin-booking"
      ? "Offline booking"
      : bar.source === "guest-booking"
      ? "Approved booking (no contract yet)"
      : bar.source === "pending-booking"
      ? "Pending booking — not blocking"
      : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => onBarClick(bar)}
          className={cn(
            "absolute h-10 z-20 cursor-pointer",
            "flex items-center px-2.5 overflow-hidden",
            "transition-all hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
            barStyle(bar),
            // Rounding: partial edges get flat
            bar.isPartialStart ? "rounded-r rounded-l-none" : "rounded-l",
            bar.isPartialEnd ? "rounded-l rounded-r-none" : "rounded-r",
            !bar.isPartialStart && !bar.isPartialEnd && "rounded"
          )}
          style={{
            left: bar.leftPx,
            width: bar.widthPx,
            top: ROW_PAD_PX + (bar.lane ?? 0) * BAR_LANE_PX,
          }}
          aria-label={`${bar.studentName}: ${bar.roomTypeName}`}
        >
          <span className="text-xs font-medium truncate leading-none">
            {bar.studentName}
          </span>
          {/* Real arrival→departure strip inside the month-granular billing bar */}
          {bar.actualWidthPx !== undefined && bar.actualLeftPx !== undefined && (
            <span
              className="absolute bottom-0.5 h-1 rounded-full bg-foreground/35 pointer-events-none"
              style={{
                left: bar.actualLeftPx - parseFloat(bar.leftPx),
                width: bar.actualWidthPx,
              }}
            />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs space-y-0.5">
        {kind && <p className="font-semibold uppercase tracking-wide text-[10px] opacity-70">{kind}</p>}
        <p className="font-semibold">{bar.studentName}</p>
        <p className="text-muted-foreground">
          {format(bar.startDate, "dd/MM/yy")} – {format(bar.endDate, "dd/MM/yy")}
        </p>
        {bar.actualStart && bar.actualEnd && (
          <p className="text-muted-foreground">
            Arrives {format(bar.actualStart, "dd/MM/yy")} · leaves {format(bar.actualEnd, "dd/MM/yy")}
          </p>
        )}
        <p className="text-muted-foreground">{bar.roomTypeName}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// ─── Gantt Row ─────────────────────────────────────────────────────────────────

interface GanttRowProps {
  row: RoomRow;
  currentMonthIndex: number | null;
  onBarClick: (bar: OccupancyBar) => void;
  onAddForMonth: (row: RoomRow, monthIndex: number) => void;
}

/** Vertical month column dividers spanning a row's height. */
function MonthColumns({ currentMonthIndex }: { currentMonthIndex: number | null }) {
  return (
    <>
      {MONTH_LABELS.map((label, i) => (
        <div
          key={label}
          className={cn(
            "absolute top-0 bottom-0 border-r last:border-r-0 pointer-events-none",
            currentMonthIndex === i && "bg-secondary/5"
          )}
          style={{ left: i * MONTH_WIDTH_PX, width: MONTH_WIDTH_PX }}
        />
      ))}
    </>
  );
}

function GanttRow({ row, currentMonthIndex, onBarClick, onAddForMonth }: GanttRowProps) {
  const todayVisible = currentMonthIndex !== null;
  const todayPx =
    currentMonthIndex !== null ? currentMonthIndex * MONTH_WIDTH_PX + MONTH_WIDTH_PX / 2 : 0;

  return (
    <div
      className="relative border-b last:border-b-0 hover:bg-muted/10 transition-colors group"
      style={{ height: rowHeightPx(row) }}
    >
      {/* Month column dividers + current-month tint */}
      <MonthColumns currentMonthIndex={currentMonthIndex} />

      {/* Clickable empty month cells — open create dialog prefilled to that month */}
      {MONTH_LABELS.map((label, i) => (
        <button
          key={label}
          type="button"
          onClick={() => onAddForMonth(row, i)}
          className="absolute top-0 bottom-0 z-0 focus:outline-none focus:ring-1 focus:ring-ring/40"
          style={{ left: i * MONTH_WIDTH_PX, width: MONTH_WIDTH_PX }}
          aria-label={`Block or book ${row.roomTypeName} in ${label}`}
          title="Block or book this month"
        />
      ))}

      {/* Today indicator */}
      {todayVisible && (
        <div
          className="absolute top-0 bottom-0 w-px bg-secondary/40 z-10 pointer-events-none"
          style={{ left: todayPx }}
        />
      )}

      {/* Occupancy bars (contracts, admin blocks, offline bookings) */}
      {row.bars.map(bar => (
        <ContractBar key={bar.barId} bar={bar} onBarClick={onBarClick} />
      ))}

      {/* Available indicator (no active bars) */}
      {!row.isOccupiedInMonth && row.bars.length === 0 && (
        <div className="absolute inset-0 flex items-center pointer-events-none px-3">
          <div className="h-px w-full border-t border-dashed border-muted-foreground/20" />
        </div>
      )}
    </div>
  );
}

// ─── Gantt Group Row (collapsed typology header — chrome only, no bars) ─────────

interface GanttGroupRowProps {
  currentMonthIndex: number | null;
}

function GanttGroupRow({ currentMonthIndex }: GanttGroupRowProps) {
  const todayVisible = currentMonthIndex !== null;
  const todayPx =
    currentMonthIndex !== null ? currentMonthIndex * MONTH_WIDTH_PX + MONTH_WIDTH_PX / 2 : 0;

  return (
    <div className="relative h-14 border-b last:border-b-0 bg-muted/40">
      <MonthColumns currentMonthIndex={currentMonthIndex} />
      {todayVisible && (
        <div
          className="absolute top-0 bottom-0 w-px bg-secondary/40 z-10 pointer-events-none"
          style={{ left: todayPx }}
        />
      )}
    </div>
  );
}

// ─── Gantt Grid ────────────────────────────────────────────────────────────────

const LABEL_COL_WIDTH = "w-36 sm:w-48";

/** Hover-revealed "+" affordance to block or book a specific unit. */
function AddUnitButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
      title="Block or book this unit"
      aria-label="Block or book this unit"
    >
      <Plus className="h-4 w-4" />
    </Button>
  );
}

interface GanttGridProps {
  displayRows: DisplayRow[];
  currentMonthIndex: number | null;
  onBarClick: (bar: OccupancyBar) => void;
  onToggleTypology: (gkey: string) => void;
  onAddForUnit: (row: RoomRow) => void;
  onAddForMonth: (row: RoomRow, monthIndex: number) => void;
}

function GanttGrid({ displayRows, currentMonthIndex, onBarClick, onToggleTypology, onAddForUnit, onAddForMonth }: GanttGridProps) {
  const timelineWidth = 12 * MONTH_WIDTH_PX;

  return (
    <div className="flex overflow-hidden rounded-b-lg">
      {/* Sticky room label column */}
      <div className={cn("flex-shrink-0 border-r", LABEL_COL_WIDTH)}>
        {/* Header aligns with MonthHeader height */}
        <div className="h-9 border-b bg-muted/20 flex items-center px-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Room
          </span>
        </div>
        {displayRows.map(dr => {
          if (dr.kind === "group") {
            return (
              <button
                key={dr.gkey}
                type="button"
                onClick={() => onToggleTypology(dr.gkey)}
                className="h-14 w-full border-b last:border-b-0 flex flex-col justify-center px-3 bg-muted/40 text-left"
              >
                <span className="text-xs text-muted-foreground truncate leading-tight">
                  {dr.rep.residenceName}
                </span>
                <span className="text-sm font-medium leading-tight truncate flex items-center gap-1">
                  {dr.expanded
                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                  {dr.rep.roomTypeName}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-secondary leading-tight truncate">
                  <Home className="h-3 w-3 shrink-0" />
                  {dr.typologyKey} · {dr.unitCount} unit{dr.unitCount > 1 ? "s" : ""}
                </span>
              </button>
            );
          }
          if (dr.kind === "unit") {
            return (
              <div
                key={dr.row.key}
                className="border-b last:border-b-0 flex items-center justify-between pl-8 pr-2 group"
                style={{ height: rowHeightPx(dr.row) }}
              >
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                  {dr.number}
                </span>
                <AddUnitButton onClick={() => onAddForUnit(dr.row)} />
              </div>
            );
          }
          return (
            <div
              key={dr.row.key}
              className="border-b last:border-b-0 flex items-center justify-between gap-1 px-3 group"
              style={{ height: rowHeightPx(dr.row) }}
            >
              <div className="min-w-0 flex flex-col justify-center">
                <span className="text-xs text-muted-foreground truncate leading-tight">
                  {dr.row.residenceName}
                </span>
                <span className="text-sm font-medium leading-tight truncate">
                  {dr.row.roomTypeName}
                </span>
                <span className="text-xs text-muted-foreground truncate leading-tight">
                  {dr.row.residenceAddress}
                </span>
              </div>
              <AddUnitButton onClick={() => onAddForUnit(dr.row)} />
            </div>
          );
        })}
      </div>

      {/* Scrollable timeline */}
      <ScrollArea className="flex-1 overflow-hidden">
        <div style={{ width: timelineWidth }}>
          <MonthHeader currentMonthIndex={currentMonthIndex} />
          {displayRows.map(dr => {
            if (dr.kind === "group") {
              return (
                <GanttGroupRow
                  key={dr.gkey}
                  currentMonthIndex={currentMonthIndex}
                />
              );
            }
            return (
              <GanttRow
                key={dr.row.key}
                row={dr.row}
                currentMonthIndex={currentMonthIndex}
                onBarClick={onBarClick}
                onAddForMonth={onAddForMonth}
              />
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}

// ─── Contract Detail Dialog ────────────────────────────────────────────────────

interface ContractDetailDialogProps {
  bar: OccupancyBar | null;
  /** All bookings, to recover the guest details behind each contract. */
  bookings: Booking[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The booking a contract came from, so the contract dialog can show everything
 * the student filled in when they booked. `studentIds` is the reliable link;
 * bookings approved before that flow existed fall back to an email match.
 */
function findBookingForStudent(bookings: Booking[], student: StudentInfo): Booking | undefined {
  const byStudentId = bookings.find((b) => b.studentIds?.includes(student.studentId));
  if (byStudentId) return byStudentId;
  const email = student.studentEmail?.toLowerCase();
  if (!email) return undefined;
  return bookings.find((b) => b.guests.some((g) => g.email?.toLowerCase() === email));
}

function StudentSection({
  student,
  index,
  total,
  booking,
}: {
  student: StudentInfo;
  index: number;
  total: number;
  booking?: Booking;
}) {
  // Prefer the guest entry matching this student; a shared booking has one per
  // occupant. Same fallback as the contract form's NIF pre-fill.
  const guest =
    booking?.guests.find(
      (g) => g.email?.toLowerCase() === student.studentEmail?.toLowerCase()
    ) ?? booking?.guests[0];

  return (
    <div className={cn("pt-3", index > 0 && "border-t")}>
      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground" />
        {total > 1 ? `Student ${index + 1}` : "Student"}
      </h4>
      <dl className="space-y-1.5">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="font-medium text-right">{student.studentName}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Email</dt>
          <dd className="text-right break-all">{student.studentEmail}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Phone</dt>
          <dd className="text-right">{student.contactPhone || "—"}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Period</dt>
          <dd className="text-right">
            {format(student.startDate, "dd/MM/yyyy")} – {format(student.endDate, "dd/MM/yyyy")}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Monthly Rent</dt>
          <dd className="font-medium">€{student.monthlyValue}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">kWh Limit/Month</dt>
          <dd>{student.monthlyKwhLimit} kWh</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Status</dt>
          <dd>
            <span
              className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                student.contractStatus === "active"
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {student.contractStatus === "active" ? "Active" : "Terminated"}
            </span>
          </dd>
        </div>
      </dl>

      {/* Everything the student filled in at booking. Absent on bookings made
          before the DEV2-1038 write-path fix, until the student completes it
          from the portal. */}
      {guest && (
        <div className="mt-3 border-t pt-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Booking details
          </p>
          <GuestDetailsBlock guests={[guest]} showHeading={false} showContact={false} />
        </div>
      )}
    </div>
  );
}

function ContractDetailDialog({ bar, bookings, open, onOpenChange }: ContractDetailDialogProps) {
  if (!bar) return null;

  const students = bar.students ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Contract Details</DialogTitle>
          <DialogDescription>
            {bar.residenceName} · {bar.roomTypeName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Room */}
          <div>
            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" /> Room
            </h4>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Residence</dt>
                <dd className="font-medium text-right">{bar.residenceName}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Room</dt>
                <dd className="text-right">{bar.roomTypeName}</dd>
              </div>
            </dl>
          </div>

          {/* Students */}
          {students.map((student, i) => (
            <StudentSection
              key={student.contractId}
              student={student}
              index={i}
              total={students.length}
              booking={findBookingForStudent(bookings, student)}
            />
          ))}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {students.length === 1 && (
            <Button asChild onClick={() => onOpenChange(false)}>
              <Link to={`/admin/contracts/${students[0].contractId}`}>
                <FileText className="h-4 w-4 mr-2" />
                View Full Contract
              </Link>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Admin Entry Dialog (block / offline booking — view + delete) ──────────────

interface AdminEntryDialogProps {
  bar: OccupancyBar | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (bar: OccupancyBar) => void;
}

function AdminEntryDialog({ bar, open, onOpenChange, onEdit }: AdminEntryDialogProps) {
  const deleteAdminBooking = useDeleteAdminBooking();
  if (!bar) return null;

  const isBlock = bar.source === "admin-block";

  const handleDelete = async () => {
    try {
      await deleteAdminBooking.mutateAsync(bar.barId);
      sonnerToast.success(isBlock ? "Block removed" : "Offline booking deleted");
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete entry";
      sonnerToast.error("Could not delete entry", { description: message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isBlock ? "Availability block" : "Offline booking"}</DialogTitle>
          <DialogDescription>
            {bar.residenceName} · {bar.roomTypeName}
          </DialogDescription>
        </DialogHeader>

        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{isBlock ? "Label" : "Guest"}</dt>
            <dd className="font-medium text-right">{bar.studentName}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Period</dt>
            <dd className="text-right">
              {format(bar.startDate, "dd/MM/yyyy")} – {format(bar.endDate, "dd/MM/yyyy")}
            </dd>
          </div>
          {bar.adminNote && (
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Note</dt>
              <dd className="text-right">{bar.adminNote}</dd>
            </div>
          )}
        </dl>

        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteAdminBooking.isPending}
            className="sm:mr-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {isBlock ? "Remove block" : "Delete booking"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleteAdminBooking.isPending}>
            Close
          </Button>
          <Button onClick={() => onEdit(bar)} disabled={deleteAdminBooking.isPending}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Guest Booking Dialog ─────────────────────────────────────────────────────
// Opened by clicking a guest (approved/pending) booking bar. Shows the booking
// details and, for pendings, offers Approve / Reject in place — same flow and
// hooks as the Bookings page, so the conflict gate and emails apply here too.

interface GuestBookingDialogProps {
  booking: Booking | null;
  residenceName: string;
  roomLabel: string;
  /** Sibling units of the booking's typology (calendar order), for reassignment. */
  unitOptions: Array<{ id: string; label: string; price?: number }>;
  currentRoomPrice?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  rejectionComment: string;
  onRejectionCommentChange: (value: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onMoveUnit: (targetRoomTypeId: string) => void;
}

function GuestBookingDialog({
  booking,
  residenceName,
  roomLabel,
  unitOptions,
  currentRoomPrice,
  open,
  onOpenChange,
  busy,
  rejectionComment,
  onRejectionCommentChange,
  onApprove,
  onReject,
  onMoveUnit,
}: GuestBookingDialogProps) {
  if (!booking) return null;
  const isPending = booking.status === "pending";

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Booking
            <Badge
              variant="outline"
              className={
                isPending
                  ? "bg-orange-50 text-orange-700 border-orange-300"
                  : "bg-emerald-100 text-emerald-800 border-emerald-200"
              }
            >
              {isPending ? "Pending" : "Approved"}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {residenceName} · {roomLabel}
          </DialogDescription>
        </DialogHeader>

        <GuestDetailsBlock guests={booking.guests} />

        <dl className="space-y-2 text-sm border-t pt-3">
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">Billing period</dt>
            <dd className="text-right">
              {format(booking.checkIn.toDate(), "PPP")} – {format(booking.checkOut.toDate(), "PPP")}
            </dd>
          </div>
          {booking.arrivalDate && (
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Arrival</dt>
              <dd className="font-medium">{format(booking.arrivalDate.toDate(), "PPP")}</dd>
            </div>
          )}
          {booking.departureDate && (
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Departure</dt>
              <dd className="font-medium">{format(booking.departureDate.toDate(), "PPP")}</dd>
            </div>
          )}
          {booking.reference && (
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Reference</dt>
              <dd className="font-mono">{booking.reference}</dd>
            </div>
          )}
          {booking.notes && (
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Notes</dt>
              <dd className="text-right">{booking.notes}</dd>
            </div>
          )}
          {unitOptions.length > 1 && (
            <div className="flex justify-between gap-2 items-center">
              <dt className="text-muted-foreground">Room</dt>
              <dd>
                <Select
                  value={booking.roomTypeId}
                  onValueChange={(v) => v !== booking.roomTypeId && onMoveUnit(v)}
                  disabled={busy}
                >
                  <SelectTrigger className="h-8 w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {unitOptions.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.label}
                        {u.price != null ? ` · €${u.price}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </dd>
            </div>
          )}
        </dl>
        {unitOptions.length > 1 && (
          <p className="text-xs text-muted-foreground -mt-1">
            Moving keeps the same billing dates. Reassign to any free room in this residence.
          </p>
        )}

        {isPending && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Rejection reason (optional, sent to the guest)</p>
            <Textarea
              value={rejectionComment}
              onChange={(e) => onRejectionCommentChange(e.target.value)}
              placeholder="e.g. Unit no longer available for these dates"
              rows={2}
              disabled={busy}
            />
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          {isPending ? (
            <>
              <Button variant="destructive" onClick={onReject} disabled={busy} className="sm:mr-auto">
                Reject
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                Close
              </Button>
              <Button onClick={onApprove} disabled={busy}>
                {busy ? "Working…" : "Approve"}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Mobile List View ─────────────────────────────────────────────────────────

interface MobileRoomListProps {
  displayRows: DisplayRow[];
  onBarClick: (bar: OccupancyBar) => void;
  onToggleTypology: (gkey: string) => void;
  onAddForUnit: (row: RoomRow) => void;
}

function barBadge(bar: OccupancyBar): { label: string; className: string } {
  switch (bar.source) {
    case "admin-block":
      return { label: "Blocked", className: "bg-slate-200 text-slate-700 border-slate-300" };
    case "admin-booking":
      return { label: "Offline booking", className: "bg-amber-100 text-amber-800 border-amber-200" };
    case "guest-booking":
      return { label: "Booked", className: "bg-emerald-100 text-emerald-800 border-emerald-200" };
    case "pending-booking":
      return { label: "Pending", className: "bg-orange-50 text-orange-700 border-orange-300 border-dashed" };
    case "contract":
    default:
      return bar.contractStatus === "active"
        ? { label: "Occupied", className: "bg-secondary text-secondary-foreground hover:bg-secondary/90" }
        : { label: "Terminated", className: "text-muted-foreground" };
  }
}

function MobileRowStatus({ row, onBarClick }: { row: RoomRow; onBarClick: (bar: OccupancyBar) => void }) {
  if (row.bars.length === 0) {
    return (
      <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 flex-shrink-0">
        Available
      </Badge>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      {row.bars.map(bar => {
        const badge = barBadge(bar);
        return (
          <button key={bar.barId} onClick={() => onBarClick(bar)} className="text-right">
            <Badge variant="outline" className={badge.className}>
              {badge.label}
            </Badge>
            <p className="text-xs text-muted-foreground mt-0.5">{bar.studentName}</p>
            <p className="text-xs text-muted-foreground">
              {format(bar.startDate, "dd/MM/yy")} – {format(bar.endDate, "dd/MM/yy")}
            </p>
          </button>
        );
      })}
    </div>
  );
}

function MobileRoomList({ displayRows, onBarClick, onToggleTypology, onAddForUnit }: MobileRoomListProps) {
  return (
    <div className="divide-y">
      {displayRows.map(dr => {
        if (dr.kind === "group") {
          return (
            <button
              key={dr.gkey}
              type="button"
              onClick={() => onToggleTypology(dr.gkey)}
              className="w-full px-4 py-3 flex items-center justify-between gap-3 bg-muted/40 text-left"
            >
              <div className="min-w-0 flex items-center gap-2">
                {dr.expanded
                  ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground truncate">{dr.rep.residenceName}</p>
                  <p className="font-medium text-sm truncate">{dr.rep.roomTypeName}</p>
                </div>
              </div>
              <Badge variant="secondary" className="gap-1 bg-secondary/10 text-secondary shrink-0">
                <Home className="h-3 w-3" />
                {dr.typologyKey} · {dr.unitCount}
              </Badge>
            </button>
          );
        }
        if (dr.kind === "unit") {
          return (
            <div
              key={dr.row.key}
              className="pl-10 pr-4 py-3 flex items-center justify-between gap-3"
            >
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                {dr.number}
              </span>
              <div className="flex items-center gap-2">
                <MobileRowStatus row={dr.row} onBarClick={onBarClick} />
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onAddForUnit(dr.row)} aria-label="Block or book this unit">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        }
        return (
          <div key={dr.row.key} className="px-4 py-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground truncate">{dr.row.residenceName}</p>
              <p className="font-medium text-sm truncate">{dr.row.roomTypeName}</p>
              <p className="text-xs text-muted-foreground truncate">{dr.row.residenceAddress}</p>
            </div>
            <div className="flex items-center gap-2">
              <MobileRowStatus row={dr.row} onBarClick={onBarClick} />
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onAddForUnit(dr.row)} aria-label="Block or book this unit">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const AdminAvailabilityCalendar = () => {
  const today = useMemo(() => new Date(), []);

  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [residenceFilter, setResidenceFilter] = useState<string>("all");
  const [statusView, setStatusView] = useState<"all" | "available" | "occupied">("all");
  const [contractStatus, setContractStatus] = useState<"all" | "active" | "terminated">("all");
  const [selectedBar, setSelectedBar] = useState<OccupancyBar | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [adminBar, setAdminBar] = useState<OccupancyBar | null>(null);
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<AdminBookingPrefill | undefined>();
  const [editEntry, setEditEntry] = useState<Booking | null>(null);
  const [expandedTypologies, setExpandedTypologies] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Guest-booking dialog (approve/reject in place)
  const [guestBooking, setGuestBooking] = useState<Booking | null>(null);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestRejectionComment, setGuestRejectionComment] = useState("");
  const [guestActionBusy, setGuestActionBusy] = useState(false);
  const [studentCredentials, setStudentCredentials] = useState<StudentCredential[]>([]);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const approveWithStudent = useApproveBookingWithStudent();
  const updateBookingStatus = useUpdateBookingStatus();
  const moveBookingUnit = useMoveBookingUnit();
  const { data: roomTypes = [] } = useAdminRoomTypes();

  // Sibling units of the open booking's typology, in calendar order (creation
  // order = the numbers shown on the unit rows), for the Unit reassign select.
  const guestUnitOptions = useMemo(() => {
    const rt = roomTypes.find((r) => r.id === guestBooking?.roomTypeId);
    if (!rt) return [];
    // Offer every room in the same residence (not just typology siblings), so
    // the admin can move a booking to any other room without a rebook. Label
    // by typology unit number where applicable, else the room name.
    const typologyCounters: Record<string, number> = {};
    return roomTypes
      .filter((r) => r.residenceId === rt.residenceId && r.status !== "draft")
      .sort((a, b) => {
        const t = (a.typologyKey ?? a.name).localeCompare(b.typologyKey ?? b.name);
        if (t !== 0) return t;
        return (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0);
      })
      .map((r) => {
        const price = r.priceByOccupancy?.[0];
        if (r.typologyKey) {
          typologyCounters[r.typologyKey] = (typologyCounters[r.typologyKey] ?? 0) + 1;
          return { id: r.id, label: `${r.typologyKey} · Unit ${typologyCounters[r.typologyKey]}`, price };
        }
        return { id: r.id, label: r.name, price };
      });
  }, [roomTypes, guestBooking?.roomTypeId]);

  // Price of the booking's current room, to warn when moving to a different-priced room.
  const currentRoomPrice = useMemo(
    () => roomTypes.find((r) => r.id === guestBooking?.roomTypeId)?.priceByOccupancy?.[0],
    [roomTypes, guestBooking?.roomTypeId]
  );

  const handleGuestMoveUnit = async (targetRoomTypeId: string) => {
    if (!guestBooking) return;
    // Warn if the target room's price differs from the current one — the admin
    // confirms before the booking adopts the new room (and its price).
    const target = roomTypes.find((r) => r.id === targetRoomTypeId);
    const targetPrice = target?.priceByOccupancy?.[0];
    if (
      currentRoomPrice != null &&
      targetPrice != null &&
      targetPrice !== currentRoomPrice &&
      !window.confirm(
        `This room has a different monthly price (€${targetPrice} vs €${currentRoomPrice}). ` +
          `The booking will move to it and follow the new room's price. Continue?`
      )
    ) {
      return;
    }
    setGuestActionBusy(true);
    try {
      await moveBookingUnit.mutateAsync({ booking: guestBooking, targetRoomTypeId });
      setGuestBooking({ ...guestBooking, roomTypeId: targetRoomTypeId });
      sonnerToast.success("Booking moved to another room");
    } catch (err) {
      sonnerToast.error("Could not move booking", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setGuestActionBusy(false);
    }
  };

  const { data: allBookings = [] } = useAdminBookings();

  const filters: AvailabilityFilters = useMemo(
    () => ({ residenceId: residenceFilter, statusView, contractStatus }),
    [residenceFilter, statusView, contractStatus]
  );

  const { rows: roomRows, isLoading } = useYearCalendarData(year, filters);

  const { data: residences = [] } = useAdminResidences();

  // Highlight the current month column only when the displayed year is this year.
  const currentMonthIndex =
    year === today.getFullYear() ? today.getMonth() : null;

  const occupiedCount = roomRows.filter(r => r.isOccupiedInMonth).length;
  const availableCount = roomRows.length - occupiedCount;

  // Typology rooms collapse into one row; both calendar views iterate this list.
  const displayRows = useMemo(
    () => flattenGroups(groupRoomRows(roomRows), expandedTypologies),
    [roomRows, expandedTypologies]
  );

  const toggleTypology = (gkey: string) => {
    setExpandedTypologies(prev => {
      const next = new Set(prev);
      if (next.has(gkey)) {
        next.delete(gkey);
      } else {
        next.add(gkey);
      }
      return next;
    });
  };

  const goToPrev = () => setYear(prev => prev - 1);
  const goToNext = () => setYear(prev => prev + 1);
  const goToToday = () => setYear(new Date().getFullYear());

  const handleBarClick = (bar: OccupancyBar) => {
    if (bar.source === "contract") {
      setSelectedBar(bar);
      setIsDialogOpen(true);
    } else if (bar.source === "guest-booking" || bar.source === "pending-booking") {
      // Open the in-place booking dialog (approve/reject without leaving the
      // calendar). Fall back to the Bookings page if the doc isn't loaded.
      const full = allBookings.find((b) => b.id === bar.barId);
      if (full) {
        setGuestBooking(full);
        setGuestRejectionComment("");
        setGuestDialogOpen(true);
      } else {
        navigate("/admin/bookings");
      }
    } else {
      setAdminBar(bar);
      setIsAdminDialogOpen(true);
    }
  };

  // Mirrors the Bookings page approve flow: conflict gate (throws), student
  // account creation, credentials modal, credential emails.
  const handleGuestApprove = async () => {
    if (!guestBooking) return;
    setGuestActionBusy(true);
    try {
      const { credentials: result, reassigned } = await approveWithStudent.mutateAsync({ booking: guestBooking });
      setStudentCredentials(result);
      setCredentialsModalOpen(true);
      setGuestDialogOpen(false);
      if (reassigned) {
        sonnerToast.info("Assigned to another free unit of the same typology", {
          description: "The originally assigned unit was already taken.",
        });
      }

      const residence = residences.find((r) => r.id === guestBooking.residenceId);
      const room = getRoomLabel(guestBooking.roomTypeId, roomTypes);
      const portalUrl = `${window.location.origin}/student/login`;
      const checkIn = format(guestBooking.checkIn.toDate(), "MMMM d, yyyy");
      const checkOut = format(guestBooking.checkOut.toDate(), "MMMM d, yyyy");

      const emailResults = await Promise.all(
        result.map((cred) =>
          sendBookingApprovedEmail({
            to: cred.email,
            guestName: cred.name,
            loginEmail: cred.email,
            tempPassword: cred.password,
            residenceName: residence?.name || "Unknown",
            roomTypeName: room.name,
            checkIn,
            checkOut,
            portalUrl,
          }).then((r) => ({ email: cred.email, success: r.success }))
        )
      );
      const failed = emailResults.filter((r) => !r.success).map((r) => r.email);
      if (failed.length === 0) {
        sonnerToast.success("Booking approved", {
          description: `Credentials emailed to ${emailResults.map((r) => r.email).join(", ")}`,
        });
      } else {
        sonnerToast.error("Booking approved · some emails failed", {
          description: `Failed: ${failed.join(", ")}. Use the credentials modal to share manually.`,
        });
      }
    } catch (err) {
      sonnerToast.error("Could not approve booking", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setGuestActionBusy(false);
    }
  };

  const handleGuestReject = async () => {
    if (!guestBooking) return;
    setGuestActionBusy(true);
    try {
      const trimmedComment = guestRejectionComment.trim() || undefined;
      await updateBookingStatus.mutateAsync({
        id: guestBooking.id,
        status: "rejected",
        rejectionComment: trimmedComment,
      });
      setGuestDialogOpen(false);
      sonnerToast.success("Booking rejected");

      // Send rejection emails to all guests (non-blocking, mirrors Bookings page)
      const residence = residences.find((r) => r.id === guestBooking.residenceId);
      const room = getRoomLabel(guestBooking.roomTypeId, roomTypes);
      Promise.all(
        guestBooking.guests.map((guest) =>
          sendBookingRejectionEmail({
            to: guest.email,
            guestName: guest.name,
            residenceName: residence?.name || "Unknown",
            roomTypeName: room.name,
            checkIn: format(guestBooking.checkIn.toDate(), "MMMM d, yyyy"),
            checkOut: format(guestBooking.checkOut.toDate(), "MMMM d, yyyy"),
            rejectionComment: trimmedComment,
          })
        )
      ).catch(() => {
        sonnerToast.error("Rejection saved, but some emails failed to send");
      });
    } catch (err) {
      sonnerToast.error("Could not reject booking", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setGuestActionBusy(false);
    }
  };

  const handleAddForUnit = (row: RoomRow) => {
    setEditEntry(null);
    setCreatePrefill({ residenceId: row.residenceId, roomTypeId: row.roomTypeId });
    setCreateOpen(true);
  };

  // Clicking an empty month cell opens the create dialog prefilled to the
  // first of that month; the admin refines the exact days inside the dialog.
  const handleAddForMonth = (row: RoomRow, monthIndex: number) => {
    setEditEntry(null);
    setCreatePrefill({
      residenceId: row.residenceId,
      roomTypeId: row.roomTypeId,
      checkIn: new Date(year, monthIndex, 1),
    });
    setCreateOpen(true);
  };

  const handleEditAdminEntry = (bar: OccupancyBar) => {
    const entry = allBookings.find((b) => b.id === bar.barId);
    if (!entry) return;
    setIsAdminDialogOpen(false);
    setCreatePrefill(undefined);
    setEditEntry(entry);
    setCreateOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold">Availability Calendar</h1>
          <Button
            size="sm"
            onClick={() => {
              setEditEntry(null);
              setCreatePrefill(undefined);
              setCreateOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            New block / booking
          </Button>
        </div>

        {/* Year navigation — prev / year label / next / This year */}
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" onClick={goToPrev} aria-label="Previous year">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-base font-semibold min-w-[80px] text-center tabular-nums">
            {year}
          </span>
          <Button variant="outline" size="icon" onClick={goToNext} aria-label="Next year">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday} className="ml-1">
            This year
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={residenceFilter} onValueChange={setResidenceFilter}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="All Residences" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Residences</SelectItem>
            {residences.map(r => (
              <SelectItem key={r.id} value={r.id}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={statusView}
          onValueChange={v => setStatusView(v as typeof statusView)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Rooms</SelectItem>
            <SelectItem value="occupied">Occupied Only</SelectItem>
            <SelectItem value="available">Available Only</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={contractStatus}
          onValueChange={v => setContractStatus(v as typeof contractStatus)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Contracts</SelectItem>
            <SelectItem value="active">Active Only</SelectItem>
            <SelectItem value="terminated">Terminated Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats row */}
      {!isLoading && roomRows.length > 0 && (
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block w-3 h-3 rounded-sm bg-secondary flex-shrink-0" />
            <span>{occupiedCount} occupied</span>
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block w-3 h-3 rounded-sm bg-muted border flex-shrink-0" />
            <span>{availableCount} available</span>
          </span>
        </div>
      )}

      {/* Calendar Card */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
              <span className="animate-pulse">Loading...</span>
            </div>
          ) : roomRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
              <Calendar className="h-8 w-8 opacity-30" />
              <p className="text-sm text-center px-4">
                {statusView === "occupied"
                  ? "No occupied rooms this year."
                  : statusView === "available"
                  ? "No available rooms found."
                  : "No rooms found. Add rooms in the Rooms section."}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile: list view */}
              <div className="md:hidden">
                <MobileRoomList
                  displayRows={displayRows}
                  onBarClick={handleBarClick}
                  onToggleTypology={toggleTypology}
                  onAddForUnit={handleAddForUnit}
                />
              </div>
              {/* Desktop: year Gantt timeline */}
              <div className="hidden md:block">
                <GanttGrid
                  displayRows={displayRows}
                  currentMonthIndex={currentMonthIndex}
                  onBarClick={handleBarClick}
                  onToggleTypology={toggleTypology}
                  onAddForUnit={handleAddForUnit}
                  onAddForMonth={handleAddForMonth}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-3 rounded bg-secondary" />
          Active contract
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-3 rounded bg-muted border-2 border-muted-foreground/40" />
          Terminated contract
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-3 rounded bg-slate-300 border-2 border-dashed border-slate-500" />
          Admin block
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-3 rounded bg-amber-300" />
          Offline booking
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-3 rounded bg-emerald-300" />
          Approved booking
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-3 rounded bg-orange-100 border-2 border-dashed border-orange-400" />
          Pending booking
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-0.5 h-3 bg-secondary/60" />
          Today
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-px border-t border-dashed border-muted-foreground/40" />
          Available
        </span>
      </div>

      <ContractDetailDialog
        bar={selectedBar}
        bookings={allBookings}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />

      <AdminEntryDialog
        bar={adminBar}
        open={isAdminDialogOpen}
        onOpenChange={setIsAdminDialogOpen}
        onEdit={handleEditAdminEntry}
      />

      <CreateAdminBookingDialog
        open={createOpen}
        onOpenChange={(o) => {
          setCreateOpen(o);
          if (!o) setEditEntry(null);
        }}
        prefill={createPrefill}
        editEntry={editEntry}
      />

      <GuestBookingDialog
        booking={guestBooking}
        residenceName={residences.find((r) => r.id === guestBooking?.residenceId)?.name ?? "—"}
        roomLabel={getRoomLabel(guestBooking?.roomTypeId, roomTypes).name}
        unitOptions={guestUnitOptions}
        currentRoomPrice={currentRoomPrice}
        open={guestDialogOpen}
        onOpenChange={setGuestDialogOpen}
        busy={guestActionBusy}
        rejectionComment={guestRejectionComment}
        onRejectionCommentChange={setGuestRejectionComment}
        onApprove={handleGuestApprove}
        onReject={handleGuestReject}
        onMoveUnit={handleGuestMoveUnit}
      />

      <StudentCredentialsModal
        open={credentialsModalOpen}
        onClose={() => setCredentialsModalOpen(false)}
        students={studentCredentials}
      />
    </div>
  );
};

export default AdminAvailabilityCalendar;
